import React, { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import showAlert from "../../../server/ShowAlert";
import {
  createSupplierPromotion,
  formatSupplierPriceDate,
  supplierPromoDateFromInput,
  supplierPromoDateToInput,
  updateSupplierPromotion,
} from "../../../server/SupplierPrice";
import { toDateInputValue } from "../sales/promotionUtils";

export const SUPPLIER_PROMO_TYPES = {
  FIXED_PRICE: "FIXED_PRICE",
  PERCENT: "PERCENT",
  X_FOR_Y: "X_FOR_Y",
};

export const getSupplierPromoTypeName = (promoType) => {
  switch (promoType) {
    case SUPPLIER_PROMO_TYPES.PERCENT:
      return "אחוז הנחה";
    case SUPPLIER_PROMO_TYPES.X_FOR_Y:
      return "כמות במחיר";
    case SUPPLIER_PROMO_TYPES.FIXED_PRICE:
    default:
      return "מחיר קבוע";
  }
};

export const getSupplierPromoValue = (promo) => {
  if (!promo) return "";
  const value = Number(promo.discountValue);
  const formatted = Number.isFinite(value) ? value.toFixed(2) : "";
  switch (promo.promoType) {
    case SUPPLIER_PROMO_TYPES.PERCENT:
      return `${promo.discountValue}%`;
    case SUPPLIER_PROMO_TYPES.X_FOR_Y:
      return `${promo.minQuantity ?? "?"} ב-${formatted} ₪`;
    case SUPPLIER_PROMO_TYPES.FIXED_PRICE:
    default:
      return formatted ? `${formatted} ₪` : "";
  }
};

const getPromoApiErrorMessage = (error, fallback) =>
  error?.apiErrorMessage || fallback;

const SupplierPromotionForm = ({
  promo = null,
  supplierId,
  gtin,
  publishDate,
  close,
  onSaved,
}) => {
  const isEdit = Boolean(promo?.promoId);
  const today = useMemo(() => toDateInputValue(new Date()), []);
  const [promoType, setPromoType] = useState(
    promo?.promoType || SUPPLIER_PROMO_TYPES.FIXED_PRICE,
  );
  const [discountValue, setDiscountValue] = useState(
    promo?.discountValue != null ? String(promo.discountValue) : "",
  );
  const [minQuantity, setMinQuantity] = useState(
    promo?.minQuantity != null ? String(promo.minQuantity) : "",
  );
  const [supplierDescription, setSupplierDescription] = useState(
    promo?.supplierDescription || promo?.description || "",
  );
  const [startDate, setStartDate] = useState(
    supplierPromoDateToInput(promo?.startDate) || today,
  );
  const [endDate, setEndDate] = useState(
    supplierPromoDateToInput(promo?.endDate) || today,
  );
  const [saving, setSaving] = useState(false);

  const valueLabel =
    promoType === SUPPLIER_PROMO_TYPES.PERCENT
      ? "אחוז הנחה"
      : promoType === SUPPLIER_PROMO_TYPES.X_FOR_Y
        ? "מחיר כולל"
        : "מחיר מבצע";

  const handleSave = async () => {
    const parsedValue = parseFloat(discountValue);
    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
      showAlert({ msgTitle: "ערך מבצע לא תקין", msgType: "error" });
      return;
    }
    if (promoType === SUPPLIER_PROMO_TYPES.PERCENT && parsedValue > 100) {
      showAlert({
        msgTitle: "אחוז הנחה חייב להיות בין 0 ל-100",
        msgType: "error",
      });
      return;
    }
    const parsedQty = parseInt(minQuantity, 10);
    if (
      promoType === SUPPLIER_PROMO_TYPES.X_FOR_Y &&
      (!Number.isFinite(parsedQty) || parsedQty <= 0)
    ) {
      showAlert({
        msgTitle: "למבצע מסוג כמות במחיר יש להזין כמות חיובית",
        msgType: "error",
      });
      return;
    }

    const payload = {
      promoType,
      discountValue: parsedValue,
      startDate: supplierPromoDateFromInput(startDate),
      endDate: supplierPromoDateFromInput(endDate),
      minQuantity:
        promoType === SUPPLIER_PROMO_TYPES.X_FOR_Y ? parsedQty : undefined,
      supplierDescription: supplierDescription.trim() || undefined,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateSupplierPromotion(promo.promoId, payload);
        showAlert({ msgTitle: "מבצע הספק עודכן בהצלחה", msgType: "success" });
      } else {
        await createSupplierPromotion({
          supplierId,
          gtin,
          publishDate: publishDate || formatSupplierPriceDate(),
          ...payload,
        });
        showAlert({ msgTitle: "מבצע הספק נוצר בהצלחה", msgType: "success" });
      }
      onSaved?.();
      close?.();
    } catch (error) {
      console.error("Error saving supplier promotion:", error);
      showAlert({
        msgTitle: getPromoApiErrorMessage(error, "שגיאה בשמירת מבצע ספק"),
        msgType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="compare-price-container compare-price-container--popup">
      <div className="section-card">
        <h3 className="section-title">
          {isEdit ? "עריכת מבצע ספק" : "מבצע ספק חדש"}
        </h3>

        <div className="field-group">
          <label className="field-label" htmlFor="supplier-promo-type">
            סוג מבצע
          </label>
          <select
            id="supplier-promo-type"
            className="promo-type-select"
            value={promoType}
            disabled={saving}
            onChange={(e) => setPromoType(e.target.value)}
          >
            <option value={SUPPLIER_PROMO_TYPES.FIXED_PRICE}>מחיר קבוע</option>
            <option value={SUPPLIER_PROMO_TYPES.PERCENT}>אחוז הנחה</option>
            <option value={SUPPLIER_PROMO_TYPES.X_FOR_Y}>כמות במחיר</option>
          </select>
        </div>

        <div className="field-group">
          <label className="field-label" htmlFor="supplier-promo-value">
            {valueLabel}
          </label>
          <input
            id="supplier-promo-value"
            type="number"
            step="0.01"
            min="0"
            className={`number-input highlighted${
              promoType !== SUPPLIER_PROMO_TYPES.PERCENT ? " price-input" : ""
            }`}
            placeholder="0.00"
            value={discountValue}
            disabled={saving}
            onChange={(e) => setDiscountValue(e.target.value)}
          />
        </div>

        {promoType === SUPPLIER_PROMO_TYPES.X_FOR_Y ? (
          <div className="field-group">
            <label className="field-label" htmlFor="supplier-promo-qty">
              כמות
            </label>
            <input
              id="supplier-promo-qty"
              type="number"
              step="1"
              min="1"
              className="number-input"
              placeholder="0"
              value={minQuantity}
              disabled={saving}
              onChange={(e) => setMinQuantity(e.target.value)}
            />
          </div>
        ) : null}

        <div className="field-group">
          <label className="field-label" htmlFor="supplier-promo-desc">
            תיאור ספק
          </label>
          <input
            id="supplier-promo-desc"
            type="text"
            className="number-input"
            value={supplierDescription}
            disabled={saving}
            onChange={(e) => setSupplierDescription(e.target.value)}
          />
        </div>

        <div className="field-group">
          <label className="field-label" htmlFor="supplier-promo-start">
            תוקף מ־
          </label>
          <input
            id="supplier-promo-start"
            type="date"
            className="date-input"
            value={startDate}
            disabled={saving}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="field-group">
          <label className="field-label" htmlFor="supplier-promo-end">
            תוקף עד
          </label>
          <input
            id="supplier-promo-end"
            type="date"
            className="date-input"
            value={endDate}
            disabled={saving}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="save-button-row">
          <button
            type="button"
            className="ui-btn-brand save-promo-btn"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? (
              <Loader2 size={18} strokeWidth={2.5} aria-hidden />
            ) : (
              "שמירה"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierPromotionForm;
